import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Loader2, Plus, Package, Search, User, Trash2, CheckCircle2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import { useFinance } from '@/contexts/FinanceContext';
import { useTreatmentPackages, useDeleteTreatmentPackage } from './useTreatmentPackages';
import { PackageModal } from './PackageModal';
import type { TreatmentPackageWithPatient } from './types';
import type { FinanceTransaction } from '@/types/finance';

function formatBrl(n: number): string {
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

interface PackageStats {
  paid: number;
  paidAmount: number;
  pending: number;
  pendingAmount: number;
  totalCount: number;
}

function computeStats(
  pkg: TreatmentPackageWithPatient,
  transactions: FinanceTransaction[],
): PackageStats {
  const linked = transactions.filter((t) => t.recurrenceGroupId !== undefined);
  // Como não temos treatment_package_id em FinanceTransaction (mapping), filtra por descrição.
  // (Calibração futura: expor treatment_package_id no hook de transactions.)
  const matches = transactions.filter((t) => t.description.startsWith(`${pkg.description} —`));
  const arr = matches.length > 0 ? matches : linked;
  void arr;
  const paid = matches.filter((t) => t.status === 'completed');
  const pending = matches.filter((t) => t.status !== 'completed');
  return {
    paid: paid.length,
    paidAmount: paid.reduce((s, t) => s + Number(t.amount), 0),
    pending: pending.length,
    pendingAmount: pending.reduce((s, t) => s + Number(t.amount), 0),
    totalCount: matches.length,
  };
}

function PackageCard({ pkg }: { pkg: TreatmentPackageWithPatient }) {
  const { transactions } = useFinance();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const remove = useDeleteTreatmentPackage();
  const stats = computeStats(pkg, transactions);
  const progressPct = stats.totalCount > 0 ? Math.round((stats.paid / stats.totalCount) * 100) : 0;

  return (
    <>
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <User size={12} />
                <span className="truncate font-medium text-foreground">{pkg.patient.name}</span>
                {pkg.patient.phone && <span>· {pkg.patient.phone}</span>}
              </div>
              <h3 className="mt-1 font-semibold text-foreground">{pkg.description}</h3>
            </div>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setConfirmDelete(true)}
              disabled={remove.isPending}
              className="h-7 w-7 flex-shrink-0 text-muted-foreground hover:text-expense"
              title="Excluir pacote"
            >
              <Trash2 size={13} />
            </Button>
          </div>

          <div className="grid grid-cols-3 gap-2 rounded-lg bg-secondary/50 p-2 text-xs">
            <div>
              <span className="text-muted-foreground">Total</span>
              <p className="font-semibold text-foreground">{formatBrl(Number(pkg.total_amount))}</p>
            </div>
            <div>
              <span className="text-muted-foreground">
                {pkg.down_payment > 0 ? 'Entrada + Parc.' : 'Parcelas'}
              </span>
              <p className="font-semibold text-foreground">
                {pkg.down_payment > 0
                  ? `${formatBrl(Number(pkg.down_payment))} + ${pkg.number_of_installments}×`
                  : `${pkg.number_of_installments}×`}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">Início</span>
              <p className="font-semibold text-foreground">
                {format(new Date(`${pkg.start_date}T00:00:00`), 'dd/MM/yyyy', { locale: ptBR })}
              </p>
            </div>
          </div>

          {stats.totalCount > 0 && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <span className="inline-flex items-center gap-1 text-income">
                  <CheckCircle2 size={12} />
                  {stats.paid} paga{stats.paid === 1 ? '' : 's'} · {formatBrl(stats.paidAmount)}
                </span>
                <span className="inline-flex items-center gap-1 text-muted-foreground">
                  <Clock size={12} />
                  {stats.pending} pendente{stats.pending === 1 ? '' : 's'} ·{' '}
                  {formatBrl(stats.pendingAmount)}
                </span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
                <div
                  className="h-full rounded-full bg-income transition-all"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir pacote?</AlertDialogTitle>
            <AlertDialogDescription>
              "{pkg.description}" será excluído. Parcelas ainda <strong>pendentes</strong> também
              serão removidas; parcelas já <strong>pagas</strong> serão mantidas no histórico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => remove.mutate(pkg)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function TreatmentPackagesPage() {
  const { clientId, client } = useCurrentClient();
  const { data: pkgs = [], isLoading } = useTreatmentPackages();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return pkgs;
    return pkgs.filter(
      (p) =>
        p.description.toLowerCase().includes(term) ||
        p.patient.name.toLowerCase().includes(term) ||
        p.patient.phone?.toLowerCase().includes(term),
    );
  }, [pkgs, search]);

  if (!clientId) {
    return (
      <div>
        <h1 className="mb-1 font-brand text-2xl font-bold text-foreground">
          Pacotes de tratamento
        </h1>
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            <Package className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p className="text-sm">Selecione um cliente no topo para ver os pacotes.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-brand text-2xl font-bold text-foreground">Pacotes de tratamento</h1>
          <p className="text-sm text-muted-foreground">
            {client?.name ? `${client.name} · ` : ''}pacientes e parcelas geradas
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="bg-primary font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus size={16} className="mr-2" />
          Novo pacote
        </Button>
      </div>

      <Card>
        <CardContent className="p-3">
          <div className="relative">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              placeholder="Buscar por paciente ou descrição…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center p-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            <Package className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p className="text-sm">
              {search ? 'Nenhum pacote corresponde à busca.' : 'Nenhum pacote cadastrado.'}
            </p>
            {!search && (
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => setCreateOpen(true)}
              >
                <Plus size={14} className="mr-1.5" />
                Criar primeiro pacote
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {filtered.map((pkg) => (
            <PackageCard key={pkg.id} pkg={pkg} />
          ))}
        </div>
      )}

      <PackageModal open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}
